import React, { useEffect, useState } from 'react';
import { 
    BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    Area, AreaChart
} from 'recharts';
import { fetchComprehensiveReports, fetchComprehensiveJobReports, verifyReportsPassword } from './api';
import './ComprehensiveReports.css';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#FFC658', '#FF6B6B'];

const ComprehensiveReports = ({ isAdmin = false, canSeeRevenue = false }) => {
    // Work order revenue also needs Sales Base
    const showRetail = isAdmin && canSeeRevenue;
    const [activeTab, setActiveTab] = useState(showRetail ? 'retail' : 'jobs');
    const [period, setPeriod] = useState('monthly');
    const [retailData, setRetailData] = useState(null);
    const [jobData, setJobData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [passwordVerified, setPasswordVerified] = useState(false);
    const [password, setPassword] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [verifyingPassword, setVerifyingPassword] = useState(false);

    // Check if password was already verified in this session
    useEffect(() => {
        const verified = sessionStorage.getItem('reportsPasswordVerified') === 'true';
        setPasswordVerified(verified);
    }, []);

    useEffect(() => {
        // Only fetch data if password is verified
        if (passwordVerified) {
            fetchData();
        }
    }, [period, isAdmin, showRetail, passwordVerified]);

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        setVerifyingPassword(true);
        setPasswordError('');

        try {
            const result = await verifyReportsPassword(password);
            if (result.valid) {
                setPasswordVerified(true);
                // Store password verification in session storage (expires when browser closes)
                sessionStorage.setItem('reportsPasswordVerified', 'true');
            } else {
                setPasswordError(result.message || 'Invalid password');
            }
        } catch (error) {
            setPasswordError(error.response?.data?.message || 'Failed to verify password. Please try again.');
        } finally {
            setVerifyingPassword(false);
        }
    };

    const handleLockReports = () => {
        // Clear password verification
        sessionStorage.removeItem('reportsPasswordVerified');
        setPasswordVerified(false);
        setPassword('');
        setPasswordError('');
    };

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            // Only fetch reports if user is admin
            if (isAdmin) {
                const jobReports = await fetchComprehensiveJobReports(period);
                setJobData(jobReports);
                
                if (showRetail) {
                    const retailReports = await fetchComprehensiveReports(period);
                    setRetailData(retailReports);
                } else {
                    setRetailData(null);
                }
            } else {
                setJobData(null);
                setRetailData(null);
            }
        } catch (error) {
            console.error('Error fetching comprehensive reports:', error);
            setError('Failed to load report data. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (value) => {
        if (value === undefined || value === null) return '$0.00';
        return `$${Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const formatNumber = (value) => {
        if (value === undefined || value === null) return '0';
        return Number(value).toLocaleString('en-US');
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';
        
        if (period === 'daily') {
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        } else if (period === 'weekly') {
            // For weekly, show the week start date
            const weekStart = new Date(date);
            weekStart.setDate(date.getDate() - date.getDay()); // Go to Sunday
            return `Week of ${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
        } else if (period === 'monthly') {
            return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
        } else {
            return date.toLocaleDateString('en-US', { year: 'numeric' });
        }
    };

    const CustomTooltip = ({ active, payload, label, valuePrefix = '', valueSuffix = '' }) => {
        if (active && payload && payload.length) {
            return (
                <div className="custom-tooltip">
                    <p className="label">{label}</p>
                    {payload.map((entry, index) => (
                        <p key={index} style={{ color: entry.color }}>
                            {entry.name}: {valuePrefix}{formatNumber(entry.value)}{valueSuffix}
                        </p>
                    ))}
                </div>
            );
        }
        return null;
    };

    const renderSummaryStats = () => {
        const data = activeTab === 'retail' ? retailData : jobData;
        if (!data || !data.summary_stats) return null;

        const stats = data.summary_stats;

        if (activeTab === 'retail') {
            return (
                <div className="summary-stats">
                    <div className="stat-card">
                        <div className="stat-label">Total Revenue</div>
                        <div className="stat-value">{formatCurrency(stats.total_revenue)}</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-label">Total Orders</div>
                        <div className="stat-value">{formatNumber(stats.total_orders)}</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-label">Total Wheels</div>
                        <div className="stat-value">{formatNumber(stats.total_wheels)}</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-label">Avg Order Value</div>
                        <div className="stat-value">{formatCurrency(stats.avg_order_value)}</div>
                    </div>
                </div>
            );
        } else {
            return (
                <div className="summary-stats">
                    <div className="stat-card">
                        <div className="stat-label">Total Jobs</div>
                        <div className="stat-value">{formatNumber(stats.total_jobs)}</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-label">Total Quantity</div>
                        <div className="stat-value">{formatNumber(stats.total_quantity)}</div>
                    </div>
                </div>
            );
        }
    };

    const renderRevenueTimeline = () => {
        if (activeTab !== 'retail' || !retailData || !retailData.revenue_timeline) return null;

        const data = retailData.revenue_timeline.map(item => ({
            date: formatDate(item.period),
            revenue: parseFloat(item.total_revenue || 0),
            orders: parseInt(item.total_orders || 0),
            wheels: parseInt(item.total_wheels || 0)
        }));

        return (
            <div className="chart-container">
                <h3>Revenue Timeline</h3>
                <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={data}>
                        <defs>
                            <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8}/>
                                <stop offset="95%" stopColor="#8884d8" stopOpacity={0}/>
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip content={<CustomTooltip valuePrefix="$" />} />
                        <Legend />
                        <Area type="monotone" dataKey="revenue" stroke="#8884d8" fillOpacity={1} fill="url(#colorRevenue)" name="Revenue" />
                    </AreaChart>
                </ResponsiveContainer>
            </div>
        );
    };

    const renderJobsTimeline = () => {
        if (activeTab !== 'jobs' || !jobData || !jobData.jobs_timeline) return null;

        const data = jobData.jobs_timeline.map(item => ({
            date: formatDate(item.period),
            jobs: parseInt(item.total_jobs || 0)
        }));

        return (
            <div className="chart-container">
                <h3>Jobs Timeline</h3>
                <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={data}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Line type="monotone" dataKey="jobs" stroke="#82ca9d" strokeWidth={2} name="Jobs" />
                    </LineChart>
                </ResponsiveContainer>
            </div>
        );
    };

    const renderServiceBreakdown = () => {
        const data = activeTab === 'retail' ? retailData : jobData;
        if (!data || !data.service_breakdown) return null;

        const chartData = data.service_breakdown.map(item => ({
            name: item.service_requested || item.service || 'Unknown',
            value: activeTab === 'retail' ? parseFloat(item.revenue || 0) : parseInt(item.count || 0),
            count: parseInt(item.count || 0)
        }));

        return (
            <div className="chart-container">
                <h3>Service Breakdown</h3>
                <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                        <Pie
                            data={chartData}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                        >
                            {chartData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                        </Pie>
                        <Tooltip content={({ active, payload }) => {
                            if (active && payload && payload.length) {
                                return (
                                    <div className="custom-tooltip">
                                        <p className="label">{payload[0].name}</p>
                                        <p>{activeTab === 'retail' 
                                            ? `Revenue: ${formatCurrency(payload[0].value)}` 
                                            : `Jobs: ${formatNumber(payload[0].value)}`}</p>
                                        <p>Count: {formatNumber(payload[0].payload.count)}</p>
                                    </div>
                                );
                            }
                            return null;
                        }} />
                        <Legend />
                    </PieChart>
                </ResponsiveContainer>
            </div>
        );
    };

    const renderTopCustomers = () => {
        const data = activeTab === 'retail' ? retailData : jobData;
        if (!data || !data.top_customers) return null;

        return (
            <div className="chart-container">
                <h3>Top 10 Customers</h3>
                <div className="customers-table">
                    <table>
                        <thead>
                            <tr>
                                <th>Customer</th>
                                {activeTab === 'retail' ? (
                                    <>
                                        <th>Orders</th>
                                        <th>Revenue</th>
                                        <th>Wheels</th>
                                    </>
                                ) : (
                                    <th>Jobs</th>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {data.top_customers.map((customer, index) => (
                                <tr key={index}>
                                    <td>{customer.customer__name}</td>
                                    {activeTab === 'retail' ? (
                                        <>
                                            <td>{formatNumber(customer.total_orders)}</td>
                                            <td>{formatCurrency(customer.total_revenue)}</td>
                                            <td>{formatNumber(customer.total_wheels)}</td>
                                        </>
                                    ) : (
                                        <td>{formatNumber(customer.total_jobs)}</td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    const renderAdditionalCharts = () => {
        if (activeTab === 'retail' && retailData) {
            return (
                <>
                    {retailData.payment_breakdown && (
                        <div className="chart-container">
                            <h3>Payment Method Breakdown</h3>
                            <ResponsiveContainer width="100%" height={300}>
                                <PieChart>
                                    <Pie
                                        data={retailData.payment_breakdown.map(item => ({
                                            name: item.payment_method || 'Unknown',
                                            value: parseFloat(item.revenue || 0)
                                        }))}
                                        cx="50%"
                                        cy="50%"
                                        labelLine={false}
                                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                                        outerRadius={80}
                                        fill="#8884d8"
                                        dataKey="value"
                                    >
                                        {retailData.payment_breakdown.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip content={<CustomTooltip valuePrefix="$" />} />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                    {retailData.avg_order_timeline && (
                        <div className="chart-container">
                            <h3>Average Order Value Over Time</h3>
                            <ResponsiveContainer width="100%" height={300}>
                                <LineChart data={retailData.avg_order_timeline.map(item => ({
                                    date: formatDate(item.period),
                                    avgValue: parseFloat(item.avg_order_value || 0)
                                }))}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="date" />
                                    <YAxis />
                                    <Tooltip content={<CustomTooltip valuePrefix="$" />} />
                                    <Legend />
                                    <Line type="monotone" dataKey="avgValue" stroke="#ff7300" strokeWidth={2} name="Avg Order Value" />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </>
            );
        } else if (activeTab === 'jobs' && jobData) {
            return (
                <>
                    {jobData.contact_breakdown && (
                        <div className="chart-container">
                            <h3>Contact Breakdown</h3>
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart data={jobData.contact_breakdown.map(item => ({
                                    name: item.contact || 'Unknown',
                                    count: parseInt(item.count || 0)
                                }))}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="name" />
                                    <YAxis />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Legend />
                                    <Bar dataKey="count" fill="#8884d8" name="Jobs" />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </>
            );
        }
        return null;
    };

    // Show password prompt if not verified
    if (!passwordVerified) {
        return (
            <div className="comprehensive-reports">
                <div className="password-prompt">
                    <h2>Reports Access</h2>
                    <p>Please enter the reports password to continue</p>
                    <form onSubmit={handlePasswordSubmit}>
                        <div className="password-input-group">
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Enter password"
                                disabled={verifyingPassword}
                                autoFocus
                            />
                            <button type="submit" disabled={verifyingPassword || !password}>
                                {verifyingPassword ? 'Verifying...' : 'Submit'}
                            </button>
                        </div>
                        {passwordError && (
                            <div className="password-error">{passwordError}</div>
                        )}
                    </form>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="comprehensive-reports">
                <div className="loading-message">Loading comprehensive reports...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="comprehensive-reports">
                <div className="error-message">{error}</div>
            </div>
        );
    }

    // Restrict all reports to admin users only
    if (!isAdmin) {
        return (
            <div className="comprehensive-reports">
                <div className="error-message">
                    Access Denied: You must be an administrator to view reports.
                </div>
            </div>
        );
    }

    return (
        <div className="comprehensive-reports">
            <div className="reports-header">
                <div className="reports-title-row">
                    <h1>Comprehensive Reports</h1>
                    <button className="lock-button" onClick={handleLockReports} title="Lock Reports">
                        🔒 Lock Reports
                    </button>
                </div>
                <div className="report-controls">
                    <div className="tab-buttons">
                        {showRetail && (
                            <button 
                                className={activeTab === 'retail' ? 'active' : ''} 
                                onClick={() => setActiveTab('retail')}
                            >
                                Work Orders (Retail)
                            </button>
                        )}
                        <button 
                            className={activeTab === 'jobs' ? 'active' : ''} 
                            onClick={() => setActiveTab('jobs')}
                        >
                            Jobs
                        </button>
                    </div>

                    <div className="period-buttons">
                        <button 
                            className={period === 'daily' ? 'active' : ''} 
                            onClick={() => setPeriod('daily')}
                        >
                            Daily (Last 30 Days)
                        </button>
                        <button 
                            className={period === 'weekly' ? 'active' : ''} 
                            onClick={() => setPeriod('weekly')}
                        >
                            Weekly (Last 12 Weeks)
                        </button>
                        <button 
                            className={period === 'monthly' ? 'active' : ''} 
                            onClick={() => setPeriod('monthly')}
                        >
                            Monthly (Last Year)
                        </button>
                        <button 
                            className={period === 'yearly' ? 'active' : ''} 
                            onClick={() => setPeriod('yearly')}
                        >
                            Yearly (Last 5 Years)
                        </button>
                    </div>
                </div>
            </div>

            {renderSummaryStats()}

            <div className="charts-grid">
                {activeTab === 'retail' ? renderRevenueTimeline() : renderJobsTimeline()}
                {renderServiceBreakdown()}
                {renderAdditionalCharts()}
                {renderTopCustomers()}
            </div>
        </div>
    );
};

export default ComprehensiveReports;

